<template>
    <div class="relative">
        <div class="sticky top-0 z-50 bg-white w-full h-[74px]">
            <placement-header-alt
                :placement="visiblePlacementsNonColors?.[0]"
                :placementData="currentGroupActual"
                :index="0"
                :has-error="hasLocalErrorState"
            >
                <div v-if="hasLocalErrorState"
                    class="text-red-error text-sm"
                >
                    {{ $t('SELECT_STYLE_NO_ENGRAVING') }}
                </div>
            </placement-header-alt>
        </div>

        <div v-for="(placement) in visiblePlacementsNonColors"
            :key="placement.code"
            class="relative px-[20px] lg:px-[40px]"
        >
            <div class="pt-[29.73px]"></div>

            <step-options-initials-buttons
                v-for="(option, i) in styleOptions(placement)"
                :key="option.code"
                :has-local-error="hasLocalErrorState"
                :main-placement="placement"
                :option="option"
                @unselect="handleSelectNothing"
                @select="selectActual"
                :index="i"
                :scroll-to-offset="98"
                :scroll-to-timeout="600"
                :has-error-state="hasLocalErrorState"
                background-size="auto 100%"
            >
                <transition name="cz3__fade">
                    <div
                        v-if="selectedValue(placement) === option.value && !option.raw.code.includes('NoThanks')"
                        class="mt-[24px] mb-[44px]"
                    >
                        <div v-for="(colorPlacement, colorPlacementIndex) in visiblePlacementsColors"
                            :key="colorPlacement.code"
                        >
                            <placement-header
                                :placement="colorPlacement"
                                :index="colorPlacementIndex"
                            ></placement-header>

                            <initials-colors v-if="allowColors"
                                :placement="colorPlacement"
                                :title="$t('COLORS')"
                                :colors-placement="colorPlacement.code"
                                :should-show-header="false"
                            />
                        </div>
                    </div>
                </transition>
            </step-options-initials-buttons>

            <placement-standard :placement="placement"></placement-standard>
        </div>
    </div>
</template>

<script>
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import PlacementHeader from './PlacementHeader.vue';
    import PlacementStandard from './PlacementStandard.vue';
    import InitialsColors from './InitialsColors.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            StepOptionsInitialsButtons,
            PlacementHeaderAlt,
            PlacementHeader,
            PlacementStandard,
            InitialsColors,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        watch: {
            hasLocalErrorState(val) {
                if (val) {
                    this.customizerStore.addPanelToErrorState('Toe Engraving');
                } else {
                    this.customizerStore.removePanelFromErrorState('Toe Engraving');
                }
            },
        },

        computed: {
            hasLocalErrorState() {
                return this.isDirty && !this.isComplete;
            },
            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName);
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            showGroupOverlay() {
                return this.appStore.showGroupOverlay;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            isDisabled() {
                return this.primarySelectedValue === 'Stamp-Stamp-Blank';
            },

            primarySelectedValue() {
                return this.visiblePlacementsNonColors?.[0]?.selectedValue;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacements() {
                let placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                const canColor = placements.some((placement) => placement.custom?.['v-allow-colors'] === 'yes');

                // Filter out colors.
                if (!canColor) {
                    placements = placements.filter((p) => {
                        if (p.custom?.['v-group-type'] === 'colors') {
                            return false;
                        }

                        return true;
                    });
                }

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            visiblePlacementsNonColors() {
                return this.visiblePlacements.filter((x) => !x.code.includes('Color'));
            },

            visiblePlacementsColors() {
                return this.visiblePlacements.filter((x) => x.code.includes('Color'));
            },

            allowColors() {
                return this.visiblePlacements.some((placement) => placement.custom?.['v-allow-colors'] === 'yes');
            },

            isComplete() {
                return this.customizerStore.selectionsMade?.includes('Toe Engraving');
            },
        },

        methods: {
            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                if (!this.isComplete) {
                    return;
                }
                this.appStore.setShowGroupOverlay(false);
                this.$emit('applySelections');
            },

            getPlacementVariant(placement) {
                return placement?.custom?.['v-placement-variant'];
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            selectedValue(placement) {
                return placement.selectedValue;
            },

            selectedPlaceholder(placement) {
                return placement.selectedPlaceholder;
            },

            styleOptions(placement) {
                return this.customizerStore.sortOptions(placement, this.selectedValue(placement));
            },

            handleSelectNothing() {
                const placement = this.visiblePlacementsNonColors?.[0];

                if (placement) {
                    const placeholder = placement.components.find(
                        (x) => x.placeholder
                            && this.viewController.isAvailable(x)
                            && !x.placeholderOptional,
                    );

                    if (placeholder) {
                        this.viewController.updateComponent(placement.code, placeholder.code);
                    }
                }
            },

            selectActual({ placement, option }) {
                this.viewController.updateComponent(placement, option);
            },
        },
    };
</script>
